import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { X, Send, User, MessageCircle, MoreHorizontal, ThumbsUp, Clock, Image } from "lucide-react";
import { toast } from "react-toastify";

const CampusComments = ({ postId, onClose, currentUser, onViewProfile, showPost = false }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null); // { id, author_alias, content }
  const inputRef = useRef(null);

  // PWA UX: Prevent pull-to-refresh when modal is open
  useEffect(() => {
    document.body.classList.add("no-pull-refresh");
    return () => document.body.classList.remove("no-pull-refresh");
  }, []);

  // Swipe to close logic for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 150;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isDownwardSwipe = distance > minSwipeDistance;
    if (isDownwardSwipe) {
      onClose();
    }
  };

  useEffect(() => {
    fetchComments();
    if (showPost) {
      fetchPost();
    }

    // Subscribe to new comments
    const channel = supabase
      .channel(`comments:${postId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "comments",
          filter: `post_id=eq.${postId}`,
        },
        (payload) => {
          handleRealtimeComment(payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [postId]);

  const fetchComments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("comments")
      .select("*, profiles:user_id(campus_alias, avatar_url)")
      .eq("post_id", postId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching comments:", error);
    } else {
      setComments(data || []);
    }
    setLoading(false);
  };

  const fetchPost = async () => {
    setLoadingPost(true);
    const { data, error } = await supabase
      .from("posts")
      .select("*, profiles:author_id(avatar_url, campus_alias)")
      .eq("id", postId)
      .single();

    if (!error) {
      setPost(data);
    }
    setLoadingPost(false);
  };

  const handleLikePost = async () => {
    if (!post || post.hasVoted) return;

    // Optimistic Update
    const originalUpvotes = post.upvotes;
    setPost(prev => ({ ...prev, upvotes: (prev.upvotes || 0) + 1, hasVoted: true }));

    const { data: success, error } = await supabase.rpc("vote_for_post", {
      post_id_param: postId,
      user_id_param: currentUser.id,
    });

    if (error || success === false) {
      if (success === false) {
        toast.info("You've already liked this post!");
      } else {
        toast.error("Could not save like.");
      }
      // Revert
      setPost(prev => ({ ...prev, upvotes: originalUpvotes, hasVoted: false }));
    }
  };

  const handleRealtimeComment = async (newCommentRecord) => {
    // Need to fetch profile for the new comment to display correctly
    const { data } = await supabase
      .from("profiles")
      .select("campus_alias, avatar_url")
      .eq("id", newCommentRecord.user_id)
      .single();

    const commentWithProfile = { ...newCommentRecord, profiles: data };

    setComments((prev) => {
      // Deduplication: Check if this realtime comment matches an existing optimistic one
      const optimisticMatchIndex = prev.findIndex(
        (c) =>
          c.isOptimistic &&
          c.user_id === newCommentRecord.user_id &&
          c.content === newCommentRecord.content
      );

      if (optimisticMatchIndex !== -1) {
        const updated = [...prev];
        updated[optimisticMatchIndex] = commentWithProfile;
        return updated;
      }

      if (prev.find((c) => c.id === newCommentRecord.id)) return prev;
      return [...prev, commentWithProfile];
    });
  };

  const calculateTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const handleSubmit = async () => {
    if (!newComment.trim()) return;

    const content = newComment.trim();
    // Reset immediately for better UX
    setNewComment("");
    setReplyTo(null);

    // Optimistic Update
    const optimisticComment = {
      id: Date.now(),
      content: content,
      user_id: currentUser.id,
      created_at: new Date().toISOString(),
      parent_id: replyTo ? replyTo.id : null,
      profiles: { campus_alias: currentUser.alias, avatar_url: null }, // Avatar might need better handling if we had it
      isOptimistic: true,
    };
    setComments((prev) => [...prev, optimisticComment]);

    const { error } = await supabase.from("comments").insert([
      {
        post_id: postId,
        user_id: currentUser.id,
        content: content,
        parent_id: replyTo ? replyTo.id : null,
      },
    ]);

    if (error) {
      toast.error("Failed to post comment.");
      console.error(error);
      // Rollback
      setComments((prev) => prev.filter((c) => c.id !== optimisticComment.id));
      setNewComment(content); // Restore text
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    inputRef.current?.focus();
  };

  // Group comments into threads (parent -> children)
  const rootComments = comments.filter((c) => !c.parent_id);
  const getReplies = (parentId) =>
    comments.filter((c) => c.parent_id === parentId);

  const CommentItem = ({ comment, isReply = false }) => (
    <div
      className={`flex gap-4 mb-6 animate-in fade-in slide-in-from-left-2 duration-500 ${
        isReply ? "ml-12" : ""
      }`}
    >
      {/* Avatar */}
      <div
        className="shrink-0 transition-transform hover:scale-110 active:scale-90"
        onClick={() => onViewProfile && onViewProfile(comment.user_id)}
      >
        {comment.profiles?.avatar_url ? (
          <img
            src={comment.profiles.avatar_url}
            alt="Avatar"
            className={`${
              isReply ? "w-8 h-8" : "w-10 h-10"
            } rounded-xl object-cover border border-slate-100 dark:border-indigo-500/30 shadow-sm`}
          />
        ) : (
          <div
            className={`${
              isReply ? "w-8 h-8" : "w-10 h-10"
            } rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-600 flex items-center justify-center text-white font-black text-xs border border-indigo-200 dark:border-indigo-400`}
          >
            {comment.profiles?.campus_alias?.[0] || "?"}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="bg-slate-50 dark:bg-white/5 dark:backdrop-blur-sm rounded-2xl px-4 sm:px-5 py-2.5 sm:py-3 inline-block max-w-full border border-slate-100 dark:border-indigo-500/20 shadow-sm">
          <h4
            className="font-black text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-400 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors uppercase tracking-widest mb-1"
            onClick={() => onViewProfile && onViewProfile(comment.user_id)}
          >
            {comment.profiles?.campus_alias || "Anonymous"}
          </h4>
          <p className="text-[13px] sm:text-[14px] text-slate-800 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words font-medium">
            {comment.content}
          </p>
        </div>

        <div className="flex items-center gap-3 sm:gap-4 mt-1.5 sm:mt-2 ml-1">
          <span className="text-[8px] sm:text-[9px] text-slate-400 dark:text-slate-400 font-bold uppercase tracking-widest">
            {calculateTimeAgo(comment.created_at)}
          </span>
          <button
            onClick={() => handleReply(comment)}
            className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors"
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-500"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div 
        className="relative bg-white dark:bg-gradient-to-b dark:from-[#0f1535] dark:to-[#0a0e27] w-full md:max-w-xl h-[90vh] md:h-[85vh] md:rounded-[2rem] rounded-t-[2rem] shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-full md:slide-in-from-bottom-12 duration-700 transition-all border border-slate-100 dark:border-indigo-500/20"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header - More appropriate for mobile */}
        <div className="flex flex-col items-center pt-2 pb-0 md:hidden">
          <div className="w-10 h-1 bg-slate-200 dark:bg-indigo-500/20 rounded-full mb-1"></div>
        </div>

        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-100 dark:border-indigo-500/10 z-10 bg-white/80 dark:bg-[#0f1535]/80 backdrop-blur-2xl sticky top-0">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <h3 className="font-black text-lg sm:text-xl text-slate-900 dark:text-white tracking-tight">
              Discussion
            </h3>
            <span className="bg-indigo-50 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 px-2.5 py-1 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-500/30 shrink-0">
              {comments.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-white/10 rounded-2xl transition-all text-slate-400 dark:text-slate-400 shrink-0"
            aria-label="Close"
          >
            <X size={22} className="sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar bg-transparent">
          {/* Post Content Header (If showPost is true) */}
          {showPost && post && (
            <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-indigo-500/10 animate-in fade-in slide-in-from-top-4 duration-500 bg-white/30 dark:bg-white/5">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="shrink-0" onClick={() => onViewProfile && onViewProfile(post.author_id)}>
                    {post.profiles?.avatar_url ? (
                      <img src={post.profiles.avatar_url} className="w-10 h-10 rounded-xl object-cover border border-slate-100 dark:border-indigo-500/30" alt="" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-sm border border-indigo-200">
                        {post.author_alias?.[0]}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-black text-sm text-slate-900 dark:text-white leading-tight">{post.author_alias}</h4>
                    <span className="text-[9px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-0.5 block">
                      {calculateTimeAgo(post.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-slate-800 dark:text-slate-200 text-[14px] sm:text-[15px] leading-relaxed font-medium whitespace-pre-wrap mb-4">
                {post.content}
              </p>

              {post.image_url && (
                <div className="rounded-2xl overflow-hidden border border-slate-100 dark:border-indigo-500/10 mb-4 shadow-sm bg-slate-50 dark:bg-black/20">
                  <img src={post.image_url} className="w-full h-auto max-h-[300px] object-cover" alt="Attachment" />
                </div>
              )}

              <div className="flex items-center gap-4">
                <button 
                  onClick={handleLikePost}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${
                    post.hasVoted 
                      ? "text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/20" 
                      : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5"
                  }`}
                >
                  <ThumbsUp size={14} />
                  <span>{post.upvotes || 0} LIKES</span>
                </button>
                <div className="flex items-center gap-2 text-slate-400 text-[10px] font-black">
                  <MessageCircle size={14} />
                  <span>{comments.length} COMMENTS</span>
                </div>
              </div>
            </div>
          )}

          <div className="p-3 sm:p-5">
            {loading ? (
            <div className="space-y-4 sm:space-y-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-100 dark:bg-white/5 rounded-xl"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-2.5 sm:h-3 w-16 sm:w-20 bg-slate-100 dark:bg-white/5 rounded-full"></div>
                    <div className="h-10 sm:h-12 bg-slate-50 dark:bg-white/5 rounded-2xl"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : comments.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
              <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-[2rem] bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-4 sm:mb-6">
                <MessageCircle
                  size={28}
                  className="sm:w-8 sm:h-8 text-slate-400 dark:text-slate-500"
                />
              </div>
              <p className="text-[9px] sm:text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                No signals detected yet
              </p>
              <p className="text-[8px] sm:text-[10px] text-slate-400 dark:text-slate-500 mt-1 sm:mt-2">
                Initiate the thread below.
              </p>
            </div>
          ) : (
            <div className="space-y-2 pb-8 sm:pb-10">
              {rootComments.map((root) => (
                <div key={root.id}>
                  <CommentItem comment={root} />
                  {/* Replies */}
                  {getReplies(root.id).map((reply) => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      isReply={true}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

        {/* Footer Area - Input & Context */}
        <div className="p-3 sm:p-4 border-t border-slate-100 dark:border-indigo-500/10 bg-slate-50/50 dark:bg-white/5 backdrop-blur-xl">
          {/* Reply Indicator */}
          {replyTo && (
            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-indigo-50 dark:bg-indigo-500/20 border border-indigo-100/50 dark:border-indigo-500/30 rounded-xl flex justify-between items-center gap-2 text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-indigo-700 dark:text-indigo-300 mb-2.5 sm:mb-3 animate-in slide-in-from-bottom-2 duration-300">
              <span className="flex items-center gap-1 min-w-0">
                Replying to{" "}
                <span className="text-indigo-900 dark:text-white truncate">
                  {replyTo.profiles?.campus_alias}
                </span>
              </span>
              <button
                onClick={() => setReplyTo(null)}
                className="text-indigo-400 dark:text-indigo-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          )}

          <div className="flex items-end gap-2 bg-white dark:bg-white/10 dark:backdrop-blur-sm p-2.5 sm:p-3 rounded-[1.2rem] sm:rounded-[1.5rem] border border-slate-200 dark:border-indigo-500/20 shadow-sm focus-within:ring-4 focus-within:ring-indigo-500/5 transition-all">
            <textarea
              ref={inputRef}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={replyTo ? "Reply..." : "Add insight..."}
              className="flex-1 bg-transparent border-none outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-[13px] sm:text-[14px] font-medium resize-none max-h-28 py-1.5 sm:py-2"
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!newComment.trim()}
              className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg sm:rounded-xl hover:shadow-lg hover:shadow-indigo-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shrink-0"
            >
              <Send size={16} className="sm:w-4.5 sm:h-4.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampusComments;
